import {
	ConsultProtocolsItem,
	ConsultProtocolPacmanEnum,
	ConsultProtocolTypeEnum,
} from 'domain/usecases';

import {extractProtocolInfo} from '../components/Protocols/utils/extractProtocolInfo';
import {useConsultProtocolsTrackQuery} from '../queries/useConsultProtocolsTrackQuery';
import IconNamesType from 'presentation/icons/IconNamesType';
import {formatNumberToBRLCurrency} from 'presentation/utils/formatNumberToBRLCurrency';
import {TrackRefundStatusEnum} from '../components/Protocols/ProtocolStatusDrawer/types';
import {translations} from 'presentation/translations-new';
import {formatProtocolDate} from '../components/Protocols/utils/formatProtocolDate';
import {isTablet} from 'react-device-detect';

const {refundValue, reason, endDate} = translations['pt-br'].privateFlow.consultProtocols;
const {password} = translations['pt-br'].commons;

export function useConsultProtocolsTrack(protocol: ConsultProtocolsItem) {
	const {shouldShowDescription, protocolDescription} = extractProtocolInfo(protocol);

	const protocolType = protocol.tipoProtocolo.toUpperCase();

	const shouldFetchDetails =
		protocolType === ConsultProtocolTypeEnum.OUVIDORIA ||
		protocolType === ConsultProtocolTypeEnum.SAC ||
		protocolType === ConsultProtocolTypeEnum.FINANCEIRO ||
		protocolType === ConsultProtocolTypeEnum.ATENDIMENTO ||
		protocol.motivo === ConsultProtocolPacmanEnum.MOTIVO_8 ||
		protocol.motivo === ConsultProtocolPacmanEnum.MOTIVO_56;

	const {
		data: protocolDetails,
		isError,
		isLoading,
		refetch,
	} = useConsultProtocolsTrackQuery({
		registerNumber: protocol.nuRegistro.toString(),
		enabled: shouldFetchDetails,
	});

	const lastUpdate = protocolDetails?.trilha[0]?.dtRegistro ?? '';

	const shouldShowContent = !isError && !isLoading;

	const timelineContent =
		protocolDetails?.trilha?.map(item => ({
			title: item.dsTrilha,
			description: item.dtRegistro,
		})) ?? [];

	const shouldShowTimeline =
		timelineContent &&
		!(
			protocolType === ConsultProtocolTypeEnum.ATENDIMENTO &&
			protocolDetails?.situacao === undefined
		);

	const isServiceProtocol = protocolType === ConsultProtocolTypeEnum.ATENDIMENTO;

	const hasStatus = protocolDetails?.situacao !== undefined;

	const showDescription = shouldShowDescription && (!isServiceProtocol || !hasStatus);

	const shouldShowReason =
		protocol.motivoDescricao &&
		shouldFetchDetails &&
		(protocolDetails?.situacao === undefined ||
			protocolDetails?.situacao === TrackRefundStatusEnum.DENIED);

	const protocolDetailsList = [
		{
			iconName: 'event-filled' as IconNamesType,
			primaryText: protocol.dtEncerramento
				? formatProtocolDate(protocol.dtEncerramento, true)
				: '-',
			secondaryText: endDate,
			secondaryTextColor: 'text.secondary',
			flexDirection: 'column-reverse',
			sx: {width: isTablet ? '100%' : '85%'},
		},
		...(shouldShowReason
			? [
					{
						iconName: 'content-paste-search-filled' as IconNamesType,
						primaryText: String(protocol.motivoDescricao),
						secondaryText: reason,
						secondaryTextColor: 'text.secondary',
						flexDirection: 'column-reverse',
					},
				]
			: []),
	];

	const refundInfoList = [
		...(protocolDetails?.situacao === TrackRefundStatusEnum.APPROVED
			? [
					{
						iconName: 'key-filled' as IconNamesType,
						primaryText: protocolDetails?.senha ? String(protocolDetails?.senha) : '-',
						secondaryText: password,
						secondaryTextColor: 'text.secondary',
						flexDirection: 'column-reverse',
					},
				]
			: []),

		...(protocolDetails?.situacao === TrackRefundStatusEnum.PROCESSED
			? [
					{
						iconName: 'attach-money-filled' as IconNamesType,
						primaryText:
							typeof protocolDetails?.valorReembolso === 'number'
								? formatNumberToBRLCurrency(protocolDetails?.valorReembolso)
								: '-',
						secondaryText: refundValue,
						secondaryTextColor: 'text.secondary',
						flexDirection: 'column-reverse',
					},
				]
			: []),
	];

	return {
		protocolDetailsList,
		showDescription,
		protocolDescription,
		lastUpdate,
		shouldShowContent,
		shouldShowTimeline,
		timelineContent,
		refetch,
		isError,
		isLoading,
		refundInfoList,
		protocolDetails,
	};
}
